import {
	CONTROL,
	type DebugMetadata,
	type DebugSnapshot,
	GATE,
	MODE,
	makeControl,
	type StepMode,
	updateBreakpoints,
} from './debug/types';
import {
	type CompileEvent,
	LIMITS,
	type MemoryPage,
	type RunEvent,
	type RunResult,
} from './protocol';

export interface Compilation {
	module: WebAssembly.Module | null;
	diagnostics: string;
	debug?: DebugMetadata;
}

export class Compiler {
	private cached = new Map<boolean, { source: string; result: Compilation }>();

	async compile(
		source: string,
		signal: AbortSignal,
		onStatus: (message: string) => void,
		debug = false,
	): Promise<Compilation> {
		signal.throwIfAborted();
		const cached = this.cached.get(debug);
		if (cached?.source === source) return cached.result;
		const worker = new Worker(new URL('compiler.worker.js', document.baseURI), { type: 'module' });
		const result = await new Promise<Compilation>((resolve, reject) => {
			const cleanup = () => {
				clearTimeout(timer);
				signal.removeEventListener('abort', abort);
				worker.terminate();
			};
			const abort = () => {
				cleanup();
				reject(new DOMException('Compilation stopped', 'AbortError'));
			};
			const timer = setTimeout(() => {
				cleanup();
				reject(
					new Error(
						'Compilation timed out after 120 seconds. Try a smaller program or reload the compiler assets.',
					),
				);
			}, LIMITS.compileMs);
			signal.addEventListener('abort', abort, { once: true });
			worker.onmessage = (event: MessageEvent<CompileEvent>) => {
				const message = event.data;
				if (message.type === 'status') {
					onStatus(message.message);
					return;
				}
				cleanup();
				if (message.type === 'error') reject(new Error(message.message));
				else
					resolve({
						module: message.module,
						diagnostics: message.diagnostics,
						debug: message.debug,
					});
			};
			worker.onerror = (event) => {
				cleanup();
				reject(new Error(event.message || 'Compiler worker failed to load.'));
			};
			worker.postMessage({ type: 'compile', source, debug });
		});
		if (result.module) this.cached.set(debug, { source, result });
		return result;
	}
}

export interface DebugOptions {
	metadata: DebugMetadata;
	breakpoints: number[];
	onPause: (snapshot: DebugSnapshot) => void;
	onRunning: () => void;
}

export class RuntimeSession {
	private worker: Worker | undefined;
	private cancel: (() => void) | undefined;
	private memoryRequestId = 0;
	private control: Int32Array<SharedArrayBuffer> | undefined;
	private paused: DebugSnapshot | undefined;
	private resumeClock: (() => void) | undefined;

	constructor(private onMemory: (memory: MemoryPage | null) => void) {}

	run(
		module: WebAssembly.Module,
		stdin: string,
		timeoutMs: number,
		signal: AbortSignal,
		onOutput: (stdout: string, stderr: string) => void,
		debug?: DebugOptions,
	): Promise<RunResult> {
		signal.throwIfAborted();
		this.dispose();
		if (debug && (!crossOriginIsolated || typeof SharedArrayBuffer === 'undefined'))
			throw new Error(
				'Debugging requires cross-origin isolation (COOP/COEP headers). Run is still available.',
			);
		const control = debug ? makeControl(debug.metadata, debug.breakpoints) : undefined;
		this.control = control;
		const worker = new Worker(new URL('runner.worker.js', document.baseURI), {
			type: 'module',
		});
		this.worker = worker;
		const boundedTimeout = Math.min(
			30_000,
			Math.max(100, Number.isFinite(timeoutMs) ? timeoutMs : LIMITS.runMs),
		);
		return new Promise((resolve) => {
			let stdout = '';
			let stderr = '';
			let finished = false;
			let activeMs = 0;
			let runningSince: number | undefined;
			let timer: ReturnType<typeof setTimeout> | undefined;
			const stopClock = () => {
				clearTimeout(timer);
				if (runningSince !== undefined) activeMs += performance.now() - runningSince;
				runningSince = undefined;
			};
			const finish = (result: RunResult, terminate: boolean) => {
				if (finished) return;
				finished = true;
				stopClock();
				signal.removeEventListener('abort', abort);
				this.cancel = undefined;
				this.resumeClock = undefined;
				this.control = undefined;
				this.paused = undefined;
				if (terminate) {
					worker.terminate();
					this.worker = undefined;
				}
				resolve(result);
			};
			const interrupted = (status: RunResult['status'], error: string) => {
				stopClock();
				finish(
					{ status, error, stdout, stderr, exitCode: null, durationMs: activeMs, memoryBytes: 0 },
					true,
				);
			};
			const abort = () => interrupted('stopped', 'Execution stopped.');
			const startClock = () => {
				if (finished) return;
				runningSince = performance.now();
				timer = setTimeout(
					() =>
						interrupted(
							'timeout',
							`Execution exceeded ${boundedTimeout} ms${debug ? ' of active time' : ''}.`,
						),
					Math.max(0, boundedTimeout - activeMs),
				);
			};
			this.resumeClock = () => {
				startClock();
				debug?.onRunning();
			};
			this.cancel = abort;
			signal.addEventListener('abort', abort, { once: true });
			worker.onmessage = (event: MessageEvent<RunEvent>) => {
				if (this.worker !== worker) return;
				const message = event.data;
				if (message.type === 'memory') {
					if (message.requestId === this.memoryRequestId) this.onMemory(message.memory);
				} else if (message.type === 'output') {
					stdout = message.stdout;
					stderr = message.stderr;
					onOutput(stdout, stderr);
				} else if (message.type === 'paused') {
					stopClock();
					this.paused = message.snapshot;
					stdout = message.stdout;
					stderr = message.stderr;
					onOutput(stdout, stderr);
					this.onMemory(message.memory);
					debug?.onPause(message.snapshot);
				} else {
					onOutput(message.result.stdout, message.result.stderr);
					this.onMemory(message.memory);
					finish(message.result, false);
				}
			};
			worker.onerror = (event) =>
				interrupted('trapped', event.message || 'Runtime worker failed to load.');
			startClock();
			worker.postMessage({
				type: 'run',
				module,
				stdin,
				debug: debug && control ? { metadata: debug.metadata, control: control.buffer } : undefined,
			});
		});
	}

	resume(mode: StepMode = 'into') {
		if (!this.control || !this.paused) return;
		Atomics.store(this.control, CONTROL.depth, this.paused.frames.length);
		Atomics.store(this.control, CONTROL.mode, MODE[mode]);
		this.paused = undefined;
		this.resumeClock?.();
		Atomics.store(this.control, CONTROL.gate, GATE.resume);
		Atomics.notify(this.control, CONTROL.gate);
	}

	pause() {
		if (this.control) Atomics.store(this.control, CONTROL.mode, MODE.into);
	}

	setBreakpoints(lines: number[]) {
		if (this.control) updateBreakpoints(this.control, lines);
	}

	inspect(offset: number) {
		const requestId = ++this.memoryRequestId;
		if (this.control) {
			Atomics.store(this.control, CONTROL.offset, offset);
			Atomics.store(this.control, CONTROL.memoryRequest, requestId);
			if (this.paused) {
				Atomics.store(this.control, CONTROL.gate, GATE.memory);
				Atomics.notify(this.control, CONTROL.gate);
			}
		} else this.worker?.postMessage({ type: 'memory', offset, requestId });
	}

	dispose() {
		this.cancel?.();
		this.worker?.terminate();
		this.worker = undefined;
		this.control = undefined;
		this.paused = undefined;
		this.resumeClock = undefined;
	}
}
